import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CarePlanListItem,
  CarePlanStatus,
  CreateCarePlan,
  CreateInvoice,
  InvoiceListItem,
  InvoiceStatus,
  SettleInvoice,
  SettlementMethod,
  UpdateCarePlan,
} from '@mys/contracts';
import { api } from './api.js';

/* ============================================================================
   Invoices and care plans.
   ----------------------------------------------------------------------------
   There is no "mark paid" mutation here and there never will be. A payment
   made THROUGH Razorpay is moved to paid by a verified webhook and by nothing
   else, because the browser's success callback is a claim by the party who
   benefits from it.

   `useSettleInvoice` is a different thing wearing a similar hat: it records a
   payment that never touched the gateway — cash, UPI, a bank transfer — which
   is how most of this business is actually paid. Owner-only, and the amount is
   not part of the request. See CONTEXT.md §14 question 6.
   ========================================================================== */

export const INVOICE_STATUSES: InvoiceStatus[] = ['due', 'paid', 'refunded'];

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  due: 'Due',
  paid: 'Paid',
  refunded: 'Refunded',
};

export const INVOICE_TYPE_LABELS: Record<InvoiceListItem['type'], string> = {
  advance: 'Advance',
  final: 'Final',
  care: 'Care plan',
};

export const CARE_PLAN_STATUSES: CarePlanStatus[] = ['active', 'paused', 'cancelled'];

export const CARE_PLAN_STATUS_LABELS: Record<CarePlanStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  cancelled: 'Cancelled',
};

export interface InvoiceListResponse {
  ok: true;
  items: InvoiceListItem[];
  total: number;
  page: number;
  limit: number;
  counts: Partial<Record<InvoiceStatus, number>>;
  outstandingInr: number;
}

export function useInvoices(filters: { status?: InvoiceStatus; q?: string }, limit = 100) {
  return useQuery({
    queryKey: ['invoices', filters, limit],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.q) params.set('q', filters.q);
      params.set('limit', String(limit));
      return api.get<InvoiceListResponse>(`/api/invoices?${params.toString()}`, signal);
    },
  });
}

export function useCreateInvoice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInvoice) =>
      api.post<{ ok: true; invoice: InvoiceListItem }>('/api/invoices', input),
    onSuccess: (data) => {
      void client.invalidateQueries({ queryKey: ['invoices'] });
      // The client's own record shows their invoices and what they owe.
      void client.invalidateQueries({ queryKey: ['client', data.invoice.clientId] });
    },
  });
}

export const SETTLEMENT_METHOD_LABELS: Record<SettlementMethod, string> = {
  cash: 'Cash',
  upi: 'UPI',
  bank: 'Bank',
};

/**
 * Record a payment that arrived outside Razorpay.
 *
 * No amount in the body, deliberately — the figure stays whatever the invoice
 * says. A settle call that could also set the amount would be a discount with
 * no approval and no trail.
 */
export function useSettleInvoice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & SettleInvoice) =>
      api.post<{ ok: true; invoice: InvoiceListItem }>(`/api/invoices/${id}/settle`, input),
    onSuccess: (data) => {
      void client.invalidateQueries({ queryKey: ['invoices'] });
      // Outstanding on the client's own record moves too.
      void client.invalidateQueries({ queryKey: ['client', data.invoice.clientId] });
    },
  });
}

export interface CarePlanListResponse {
  ok: true;
  items: CarePlanListItem[];
  total: number;
  page: number;
  limit: number;
  counts: Partial<Record<CarePlanStatus, number>>;
  mrrInr: number;
}

export function useCarePlans(filters: { status?: CarePlanStatus; q?: string }, limit = 100) {
  return useQuery({
    queryKey: ['care-plans', filters, limit],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.q) params.set('q', filters.q);
      params.set('limit', String(limit));
      return api.get<CarePlanListResponse>(`/api/care-plans?${params.toString()}`, signal);
    },
  });
}

function invalidatePlans(client: ReturnType<typeof useQueryClient>, clientId?: string) {
  void client.invalidateQueries({ queryKey: ['care-plans'] });
  void client.invalidateQueries({ queryKey: ['clients'] });
  if (clientId) void client.invalidateQueries({ queryKey: ['client', clientId] });
}

export function useCreateCarePlan() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCarePlan) =>
      api.post<{ ok: true; carePlan: CarePlanListItem }>('/api/care-plans', input),
    onSuccess: (data) => invalidatePlans(client, data.carePlan.clientId),
  });
}

export function useUpdateCarePlan(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (changes: UpdateCarePlan) =>
      api.patch<{ ok: true; carePlan: CarePlanListItem }>(`/api/care-plans/${id}`, changes),
    onSuccess: (data) => invalidatePlans(client, data.carePlan.clientId),
  });
}

/* The total with GST. Defined in @mys/contracts/constants and re-exported
   here so this module stays the dashboard's one billing import, and so the
   portal and the API cannot drift from it. */
export { grossInr } from '@mys/contracts/constants';
